import {
  getDocs,
  collection,
  query,
  getDoc,
  doc,
  deleteDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  onSnapshot,
  where,
} from "firebase/firestore";
import {
  FIREBASE_AUTH,
  FIREBASE_DB,
  USE_LOCAL_DEMO_AUTH,
} from "../../firebaseConfig";
import { Post, Comment } from "../../types";
import { Dispatch, SetStateAction } from "react";
import { demoPosts, demoUser } from "../demo/localDemo";
import { fetchColumnVideos, type DesktopVideoSort } from "./videoServer";
import { ColumnVideo } from "../screens/columns";

let commentListenerInstance: (() => void) | null = null;
const USER_POSTS_CACHE_PREFIX = "kirkwat-user-posts-cache:";

function cacheUserPosts(uid: string, posts: Post[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${USER_POSTS_CACHE_PREFIX}${uid}`,
      JSON.stringify({ posts, savedAt: Date.now() }),
    );
  } catch {}
}

function cachedUserPosts(uid: string): Post[] {
  if (typeof window === "undefined") return [];
  try {
    const value = JSON.parse(
      window.localStorage.getItem(`${USER_POSTS_CACHE_PREFIX}${uid}`) || "null",
    );
    return Array.isArray(value?.posts) ? value.posts : [];
  } catch {
    return [];
  }
}

export type MobileFeedSort = Extract<
  DesktopVideoSort,
  "personalized" | "favorites" | "public"
>;

const getVideoServerPosts = async (
  sort?: MobileFeedSort,
): Promise<Post[] | null> => {
  try {
    const videos = await fetchColumnVideos(
      sort ? { surface: "mobile", sort } : {},
    );
    return columnVideosToPosts(videos).filter((post) => {
      if (!post.isAdvertisement) return true;
      if (post.adDisplayMode === "broad") return true;
      return (post.adDisplayMode || "smart") === "smart" && sort === "personalized";
    });
  } catch (error) {
    console.warn("Video server feed unavailable:", error);
    return null;
  }
};

export const columnVideosToPosts = (videos: ColumnVideo[]): Post[] =>
  videos.map((video) => ({
    id: `video-server-${video.id}`,
    videoServerId: video.id,
    favorited: video.ranking?.userSignals.favorited === true,
    creator: demoUser.uid,
    media: [
      video.videoUrl,
      video.portraitThumbnail || video.thumbnail,
      video.backgroundMediaUrl || video.videoUrl,
    ],
    mediaVariants: video.mediaVariants,
    productPlacements: video.productPlacements,
    productEditorAllowed: video.productEditorAllowed,
    isAdvertisement: video.isAdvertisement,
    adDisplayMode: video.adDisplayMode,
    adMaxPlays: video.adMaxPlays,
    adPlayCount: video.adPlayCount,
    adRemainingPlays: video.adRemainingPlays,
    adKeywords: video.adKeywords,
    channel: video.channel,
    category: video.category,
    tags: video.tags,
    rankingScore: video.rankingScore,
    ranking: video.ranking
      ? {
          personalizedScore: video.ranking.personalizedScore,
          publicScore: video.ranking.publicScore,
        }
      : undefined,
    description: video.title,
    likesCount: 0,
    commentsCount: 0,
    creation: new Date().toISOString(),
  }));

/**
 * Returns all the posts in the database.
 *
 * @returns {Promise<[<Post>]>} post list if successful.
 */

export const getFeed = (sort: MobileFeedSort = "personalized"): Promise<Post[]> => {
  return new Promise(async (resolve, reject) => {
    const videoServerPosts = await getVideoServerPosts(sort);

    if (USE_LOCAL_DEMO_AUTH) {
      resolve(videoServerPosts ?? demoPosts);
      return;
    }

    try {
      const q = query(
        collection(FIREBASE_DB, "post"),
        orderBy("creation", "desc"),
      );
      const querySnapshot = await getDocs(q);
      const posts = querySnapshot.docs.map((doc) => {
        const id = doc.id;
        const data = doc.data();
        return { id, ...data } as Post;
      });
      resolve([...(videoServerPosts ?? []), ...posts]);
    } catch (error) {
      console.error("Failed to get feed: ", error);
      reject(error);
    }
  });
};

/**
 * Gets the like state of a user in a specific post
 * @param {String} postId - id of the post
 * @param {String} uid - id of the user to get the like state of.
 *
 * @returns {Promise<Boolean>} true if user likes it and vice versa.
 */
export const getLikeById = async (postId: string, uid: string) => {
  if (USE_LOCAL_DEMO_AUTH) {
    return false;
  }

  try {
    const likeDoc = await getDoc(
      doc(FIREBASE_DB, "post", postId, "likes", uid),
    );
    return likeDoc.exists();
  } catch (error) {
    throw new Error("Could not get like");
  }
};

/**
 * Updates the like of a post according to the current user's id
 * @param {String} postId - id of the post
 * @param {String} uid - id of the user to get the like state of.
 * @param {Boolean} currentLikeState - true if the user likes the post and vice versa.
 */
export const updateLike = async (
  postId: string,
  uid: string,
  currentLikeState: boolean,
) => {
  if (USE_LOCAL_DEMO_AUTH) {
    return;
  }

  const likeDocRef = doc(FIREBASE_DB, "post", postId, "likes", uid);

  try {
    if (currentLikeState) {
      await deleteDoc(likeDocRef);
    } else {
      await setDoc(likeDocRef, {});
    }
  } catch (error) {
    throw new Error("Could not update like");
  }
};

export const addComment = async (
  postId: string,
  creator: string,
  comment: string,
) => {
  if (USE_LOCAL_DEMO_AUTH) {
    return;
  }

  try {
    await addDoc(collection(FIREBASE_DB, "post", postId, "comments"), {
      creator,
      comment,
      creation: serverTimestamp(),
    });
  } catch (e) {
    console.error("Error adding comment: ", e);
  }
};

export const commentListener = (
  postId: string,
  setCommentList: Dispatch<SetStateAction<Comment[]>>,
) => {
  if (USE_LOCAL_DEMO_AUTH) {
    setCommentList([]);
    return () => {};
  }

  const commentsQuery = query(
    collection(FIREBASE_DB, "post", postId, "comments"),
    orderBy("creation", "desc"),
  );

  const unsubscribe = onSnapshot(commentsQuery, (snapshot) => {
    if (snapshot.docChanges().length === 0) {
      return;
    }
    const comments = snapshot.docs.map((docSnapshot) => {
      const id = docSnapshot.id;
      const data = docSnapshot.data();
      return { id, ...data } as Comment;
    });
    setCommentList(comments);
  });

  return unsubscribe;
};

export const clearCommentListener = () => {
  if (commentListenerInstance != null) {
    commentListenerInstance();
    commentListenerInstance = null;
  }
};

export const getPostsByUserId = (
  uid = FIREBASE_AUTH.currentUser?.uid,
): Promise<Post[]> => {
  return new Promise((resolve, reject) => {
    if (USE_LOCAL_DEMO_AUTH) {
      getVideoServerPosts()
        .then((serverPosts) => {
          const availableServerPosts = serverPosts ?? [];
          const posts = [
            ...availableServerPosts,
            ...demoPosts.filter(
              (post) =>
                post.creator === uid &&
                !availableServerPosts.some(
                  (serverPost) => serverPost.id === post.id,
                ),
            ),
          ].filter((post) => post.creator === uid);
          cacheUserPosts(uid || demoUser.uid, posts);
          resolve(posts);
        })
        .catch(() => {
          const cached = cachedUserPosts(uid || demoUser.uid);
          const fallback = cached.length
            ? cached
            : demoPosts.filter((post) => post.creator === uid);
          resolve(fallback);
        });
      return;
    }

    if (!uid) {
      reject(new Error("User ID is not set"));
      return;
    }

    const q = query(
      collection(FIREBASE_DB, "post"),
      where("creator", "==", uid),
      orderBy("creation", "desc"),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const posts = snapshot.docs.map((doc) => {
          const data = doc.data();
          const id = doc.id;
          return { id, ...data } as Post;
        });
        cacheUserPosts(uid, posts);
        resolve(posts);
      },
      (error) => {
        const fallback = cachedUserPosts(uid);
        if (fallback.length) {
          console.warn("Using cached user posts fallback:", error);
          resolve(fallback);
        } else {
          reject(error);
        }
      },
    );

    return () => unsubscribe();
  });
};
